import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import type { PostHog } from 'posthog-js';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  buildPostHogIdentityKey,
  normalizeAnalyticsPath,
  sanitizePostHogPayload,
  withAnalyticsContext,
} from '@/lib/Analytics/productEvents';
import { useBrand } from '@/lib/brand/BrandContext';

let posthogInitialized = false;
type ProductAnalyticsClient = Pick<PostHog, 'capture'>;

const PostHogClientContext = createContext<ProductAnalyticsClient | null>(null);
let posthogLoader: Promise<{
  client: PostHog;
  PostHogProvider: React.ComponentType<{
    client: PostHog;
    children: React.ReactNode;
  }>;
}> | null = null;

const initializePostHog = async (): Promise<{
  client: PostHog;
  PostHogProvider: React.ComponentType<{
    client: PostHog;
    children: React.ReactNode;
  }>;
} | null> => {
  const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!posthogKey) return null;

  if (!posthogLoader) {
    posthogLoader = Promise.all([
      import('posthog-js'),
      import('posthog-js/react'),
    ])
      .then(([posthogModule, posthogReactModule]) => ({
        client: posthogModule.default,
        PostHogProvider: posthogReactModule.PostHogProvider,
      }))
      .catch((error) => {
        posthogLoader = null;
        throw error;
      });
  }

  const { client, PostHogProvider } = await posthogLoader;

  if (!posthogInitialized) {
    client.init(posthogKey, {
      api_host: '/ingest',
      ui_host: 'https://us.i.posthog.com',
      defaults: '2026-01-30',
      capture_pageview: false,
      capture_pageleave: false,
      autocapture: false,
      rageclick: true,
      mask_all_element_attributes: true,
      mask_all_text: true,
      mask_personal_data_properties: true,
      property_denylist: [
        'assignmentURL',
        'accessCode',
        'callbackUrl',
        'classroomDisplayName',
        'description',
        'destination',
        'displayName',
        'href',
        'label',
        'rawUrl',
        'responseBody',
        'response_excerpt',
        'responseExcerpt',
        'roomCode',
        'roomKey',
        'stack_trace',
        'stackTrace',
        'studentName',
        'teacherName',
        'text',
        'url',
      ],
      before_send: sanitizePostHogPayload,
    });
    posthogInitialized = true;
  }

  return { client, PostHogProvider };
};

export function CSPostHogProvider({ children }: { children: React.ReactNode }) {
  const brand = useBrand();
  const [providerState, setProviderState] = useState<{
    client: PostHog;
    PostHogProvider: React.ComponentType<{
      client: PostHog;
      children: React.ReactNode;
    }>;
  } | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!brand.analyticsEnabled) {
      setProviderState(null);
      return;
    }
    let mounted = true;
    void initializePostHog()
      .then((state) => {
        if (!mounted || !state) return;
        setProviderState(state);
      })
      .catch(() => {
        // Analytics should never block rendering.
      });

    return () => {
      mounted = false;
    };
  }, [brand.analyticsEnabled]);

  const productAnalyticsClient = useMemo<ProductAnalyticsClient | null>(() => {
    if (!providerState) return null;
    const { client } = providerState;
    return {
      capture: (eventName, properties, options) =>
        client.capture(
          eventName,
          withAnalyticsContext(properties, brand),
          options
        ),
    };
  }, [brand, providerState]);

  if (!providerState) {
    return (
      <PostHogClientContext.Provider value={null}>
        {children}
      </PostHogClientContext.Provider>
    );
  }

  const { client, PostHogProvider } = providerState;

  return (
    <PostHogClientContext.Provider value={productAnalyticsClient}>
      <PostHogProvider client={client}>
        <PostHogAuthWrapper client={client} brand={brand}>
          {children}
        </PostHogAuthWrapper>
      </PostHogProvider>
    </PostHogClientContext.Provider>
  );
}

export function useOptionalPostHog() {
  return useContext(PostHogClientContext);
}

function PostHogAuthWrapper({
  brand,
  children,
  client,
}: {
  brand: ReturnType<typeof useBrand>;
  children: React.ReactNode;
  client: PostHog;
}) {
  const { data, status } = useSession();
  const userInfo = data?.user;
  const hasCapturedInitialPageView = useRef(false);
  const latestIdentityKey = useRef<string | null>(null);

  useEffect(() => {
    if (userInfo?.id && status !== 'loading') {
      const identityTraits = {
        email: userInfo.email,
        name: `${userInfo.firstName} ${userInfo.lastName}`,
        role: userInfo.role,
        locale: userInfo.locale,
        hasSeenOnBoarding: userInfo.hasSeenOnBoarding,
      };
      const identityKey = buildPostHogIdentityKey(userInfo.id, identityTraits);
      if (latestIdentityKey.current === identityKey) return;
      latestIdentityKey.current = identityKey;
      client.identify(userInfo.id, identityTraits);
    } else if (status === 'unauthenticated') {
      latestIdentityKey.current = null;
      client.reset();
    }
  }, [client, userInfo, status]);

  const router = useRouter();

  useEffect(() => {
    const handleRouteChange = (url?: string) => {
      const routeTemplate = normalizeAnalyticsPath(url ?? router.asPath);
      client.capture('$pageview', {
        route_template: routeTemplate,
        ...withAnalyticsContext(null, brand),
      });
    };
    if (!hasCapturedInitialPageView.current) {
      handleRouteChange();
      hasCapturedInitialPageView.current = true;
    }

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [brand, client, router.asPath, router.events]);

  return children;
}
